"use client";

/**
 * Cancel-enrollment confirmation (Phase J · Task 57).
 *
 * Cancel is irreversible — the workflow exits cleanly with status
 * `cancelled` and the only way to re-engage is `restart`. We always
 * pass `cancelled_reason: "manual_user_cancel"` because this surface
 * is the recruiter pulling the row out of the cohort by hand. The
 * other reasons (replied / bounced / unsubscribed / etc.) are emitted
 * by automation in the workflow body, never from this UI.
 */

import { Button, SubmitButton } from "@/components/ui/button";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";

import { useEnrollmentsList } from "../../hooks/use-enrollments-list";
import type { EnrollmentListItem } from "../../lib/types";

interface CancelModalProps {
  enrollment: EnrollmentListItem | null;
  jobId: string;
  sequenceId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

export function CancelEnrollmentModal({
  enrollment,
  jobId,
  sequenceId,
  open,
  onOpenChange,
}: CancelModalProps) {
  const { cancel } = useEnrollmentsList({ jobId, sequenceId });

  const close = () => {
    if (cancel.isPending) return;
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (!enrollment) return;
    try {
      await cancel.mutateAsync({
        jobId,
        sequenceId,
        enrollmentId: enrollment.id,
        data: { cancelled_reason: "manual_user_cancel" },
      });
      showToast.success("Enrollment cancelled");
      onOpenChange(false);
    } catch (err) {
      showToast.error("Couldn't cancel the enrollment. Please try again.", {
        description: getApiErrorMessage(err),
      });
    }
  };

  return (
    <Modal open={open} onOpenChange={(next) => (!next ? close() : undefined)} dismissible={!cancel.isPending}>
      <ModalContent size="md">
        <ModalHeader title="Cancel enrollment?" iconName="IconCircleX" />
        <ModalBody>
          <p className="text-sm text-muted-foreground">
            This stops the workflow immediately. No further messages
            will be sent and the row moves to the Cancelled bucket. To
            re-engage this candidate later you&apos;ll need to use
            Restart, which creates a fresh enrollment pinned to the
            current step set.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={close}
            disabled={cancel.isPending}
          >
            Keep enrolled
          </Button>
          <SubmitButton
            type="button"
            variant="destructive"
            size="sm"
            loading={cancel.isPending}
            loadingLabel="Cancelling"
            onClick={handleConfirm}
          >
            Cancel enrollment
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
